import React from 'react'

import T from 'components/FormattedMessage'
import MainLayout from 'components/MainLayout'

import Hidden from '@material-ui/core/Hidden'

import {
  GameDetailsResponse,
  UserInfoResponse,
  GamesResponse,
  EditInvitationResponse,
  GameHistoryResponse,
  GeneralResponse,
} from 'protos'
import { withRouter, RouteComponentProps, Route } from 'react-router'
import { Action, Stream } from 'types'

import GameInviteLayer from './components/GameInviteLayer'
import PlayButton from './components/PlayButton'
import KoinIntro from './components/KoinIntro'
import ActiveGamesTab from './tabs/ActiveGamesTab'
import GameHistoryTab from './tabs/GameHistoryTab'

interface Props {
  getGameDetails: (gameId: string) => Promise<GameDetailsResponse>
  getActiveGameList: () => Promise<Stream<GamesResponse>>
  getGameHistory: () => Promise<GameHistoryResponse>
  declineInvitation: (gameId: string) => Promise<EditInvitationResponse>
  userInfo: UserInfoResponse
  isNewUser: boolean
  makeUserOld: () => void
  createGameUrl?: string
  getUrlForGameDetails: (gameId: string) => string
  acknowlegdeAllGameResult: () => Promise<GeneralResponse>
  acknowledgeQuizIntro: () => Promise<void>
  saveQuizAnswer: (questionId: string, answerId: string) => Promise<void>
}

type State = {
  activeGameList: Action<GamesResponse | null>
}

type InjectedProps = Props & RouteComponentProps<{}>

class GamesScreen extends React.Component<InjectedProps, State> {
  private activeGameListSubscription?: () => void
  private mounted: boolean

  constructor(props: InjectedProps) {
    super(props)
    this.state = { activeGameList: { isLoading: true } }
    this.mounted = false
  }

  componentDidMount() {
    this.mounted = true
    this.beginSubscription()
  }

  componentWillUnmount() {
    this.mounted = false
    this.cancelSubscriptionIfUnmounted()
  }

  private async beginSubscription() {
    this.activeGameListSubscription = await this.requestActiveGameList()
    this.cancelSubscriptionIfUnmounted()
  }

  private cancelSubscriptionIfUnmounted() {
    if (!this.mounted && this.activeGameListSubscription) {
      this.activeGameListSubscription()
      this.activeGameListSubscription = undefined
    }
  }

  private requestActiveGameList = async () => {
    const subscribe = await this.props.getActiveGameList()
    return subscribe(
      (result: GamesResponse) => {
        this.setState({ activeGameList: { isLoading: false, result }})
      },
      (err: Error) => {
        this.setState({ activeGameList: { isLoading: false, errorCode: err.message }})
      },
    )
  }

  private renderGameInviteLayer = withRouter(({ match, history }: RouteComponentProps<{ gameId: string }>) => (
    <GameInviteLayer
      getGameDetails={() => this.props.getGameDetails(match.params.gameId)}
      declineInvitation={async () => {
        const result = await this.props.declineInvitation(match.params.gameId)

        // WORKAROUND: retrieving a fresh active game list
        if (this.activeGameListSubscription) {
          this.activeGameListSubscription()
          await this.beginSubscription()
        }

        history.goBack()
        return result
      }}
      acceptInvitation={() => { history.push(`/games/${match.params.gameId}/accept`) }}
      onClose={() => history.goBack()}
      userInfo={this.props.userInfo}
    />
  ))

  private renderActiveGamesTab = () => {
    return (
      <ActiveGamesTab
        acknowledgeQuizIntro={this.props.acknowledgeQuizIntro}
        quizIntoAcked={this.props.userInfo.quizIntroAcked}
        activeGameList={this.state.activeGameList}
        createGameUrl={this.props.createGameUrl}
        getUrlForGameDetails={this.props.getUrlForGameDetails}
        saveQuizAnswer={this.props.saveQuizAnswer}
      />
    )
  }

  private renderCompletedGamesTab = () => {
    return (
      <GameHistoryTab
        getGameHistory={this.props.getGameHistory}
        acknowlegdeAllGameResult={this.props.acknowlegdeAllGameResult}
        getUrlForGameDetails={this.props.getUrlForGameDetails}
      />
    )
  }

  render() {
    const { isNewUser, makeUserOld } = this.props

    const activeTabUrl = this.props.location.pathname !== '/games/history'
      ? this.props.location.pathname
      : '/games'

    return (
      <>
        <MainLayout
          tabs={[
            {
              title: <T id="home_tab_active" />,
              url: activeTabUrl,
              component: this.renderActiveGamesTab,
              badgeContent: this.props.userInfo?.counters?.pendingGameInvites,
            },
            {
              title: <T id="home_tab_complete" />,
              url: '/games/history',
              component: this.renderCompletedGamesTab,
              badgeContent: this.props.userInfo?.counters?.unseenFinishedGames,
            },
          ]}
        />
        <KoinIntro
          open={isNewUser}
          delay={1500}
          duration={1500}
          onClose={makeUserOld}
        />
        <Route exact path="/games/:gameId/invitation" component={this.renderGameInviteLayer} />
        <Hidden xsDown>
          <PlayButton to={this.props.createGameUrl!} />
        </Hidden>
      </>
    )
  }
}

export default withRouter(GamesScreen)
try {
    // @ts-ignore
    GamesScreen.displayName = "GamesScreen";
    // @ts-ignore
    GamesScreen.__docgenInfo = { "description": "", "displayName": "GamesScreen", "props": { "userInfo": { "defaultValue": null, "description": "", "name": "userInfo", "required": true, "type": { "name": "any" } }, "makeUserOld": { "defaultValue": null, "description": "", "name": "makeUserOld", "required": true, "type": { "name": "() => void" } }, "isNewUser": { "defaultValue": null, "description": "", "name": "isNewUser", "required": true, "type": { "name": "boolean" } }, "getGameDetails": { "defaultValue": null, "description": "", "name": "getGameDetails", "required": true, "type": { "name": "(gameId: string) => Promise<any>" } }, "getActiveGameList": { "defaultValue": null, "description": "", "name": "getActiveGameList", "required": true, "type": { "name": "() => Promise<any>" } }, "getGameHistory": { "defaultValue": null, "description": "", "name": "getGameHistory", "required": true, "type": { "name": "() => Promise<any>" } }, "declineInvitation": { "defaultValue": null, "description": "", "name": "declineInvitation", "required": true, "type": { "name": "(gameId: string) => Promise<any>" } }, "createGameUrl": { "defaultValue": null, "description": "", "name": "createGameUrl", "required": false, "type": { "name": "string" } }, "getUrlForGameDetails": { "defaultValue": null, "description": "", "name": "getUrlForGameDetails", "required": true, "type": { "name": "(gameId: string) => string" } }, "acknowlegdeAllGameResult": { "defaultValue": null, "description": "", "name": "acknowlegdeAllGameResult", "required": true, "type": { "name": "() => Promise<any>" } }, "acknowledgeQuizIntro": { "defaultValue": null, "description": "", "name": "acknowledgeQuizIntro", "required": true, "type": { "name": "() => Promise<void>" } }, "saveQuizAnswer": { "defaultValue": null, "description": "", "name": "saveQuizAnswer", "required": true, "type": { "name": "(questionId: string, answerId: string) => Promise<void>" } }, "wrappedComponentRef": { "defaultValue": null, "description": "", "name": "wrappedComponentRef", "required": false, "type": { "name": "Ref<Component<InjectedProps, any, any>>" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/routes/Games/GamesScreen/GamesScreen.tsx#GamesScreen"] = { docgenInfo: GamesScreen.__docgenInfo, name: "GamesScreen", path: "src/routes/Games/GamesScreen/GamesScreen.tsx#GamesScreen" };
}
catch (__react_docgen_typescript_loader_error) { }